import {ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit, signal, Signal, untracked} from '@angular/core';
import {Store} from '@ngrx/store';
import {IHasUnsavedChanges, UnsavedChangesService} from '../../services/unsaved-changes/unsaved-changes.service';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {IPriceBandingStoreSettings, SettingsReferenceEnum} from '../../models';
import {
  selectGlobalPriceBands,
  selectIsPriceBandsLoading,
  selectIsPriceBandsSaving,
  selectSelectedSetting,
  selectSelectedStorePriceBands,
} from '../../store/setting.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {ICrudOptions, IUserAccessFeatureStockRules} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureStockRulesForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {settingsSetSelected, settingStorePriceBands} from '../../store/setting.actions';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {IPriceBand, IPriceBandSnap} from '../../../../shared/shared-models/price-banding/price-band';
import {IRange} from '../../../../shared/shared-models/range/range';
import {getDeepCopyOfObject, isNonEmptyObject} from '../../../../shared/shared-utils/object/object.utils';
import {applyPriceBanding} from '../../../../shared/shared-utils/price/price-banding.utils';
import {isValueBetweenRanges} from '../../../../shared/shared-utils/number/number.utils';
import {VatRates} from '../../../../shared-utilities/models-old/datastructures';
import {selectSharedGridVatRateBySelectedStore} from '../../../../shared-modules/shared-grid/store/shared-grid.selectors';
import {
  buildPriceBandPreviewExamples,
  clonePriceBands,
  createEmptyPriceBand,
  createEmptyPriceBandSnap,
  describePriceBandRange,
  formatPriceBandMoney,
  getPriceBandOverlapWarnings,
  validatePriceBands,
} from '../../utils/price-banding-settings.utils';

const PRICE_BANDING_PREVIEW_FALLBACK_VAT_RATES: VatRates = {[1]: {desc: 'Standard', vatRate: 15}};

type TRangeEdge = 'start' | 'end';
type TSnapKind = 'rand' | 'cents';

@Component({
  selector: 'app-feature-stock-price-banding',
  templateUrl: './feature-stock-price-banding.component.html',
  styleUrl: './feature-stock-price-banding.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureStockPriceBandingComponent implements OnInit, OnDestroy, IHasUnsavedChanges {

  private readonly formStore = inject(Store);
  private readonly modalUtils = inject(SharedModalUtils);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly settingChanged: Signal<SettingsReferenceEnum> = this.formStore.selectSignal(selectSelectedSetting);
  private readonly priceBandingView: Signal<IPriceBandingStoreSettings> = this.formStore.selectSignal(selectSelectedStorePriceBands);
  private readonly globalBands: Signal<IPriceBand[]> = this.formStore.selectSignal(selectGlobalPriceBands);
  private readonly selectedStoreSignal: Signal<IStore> = this.formStore.selectSignal(selectSelectedStore);
  private readonly vatRates: Signal<VatRates> = this.formStore.selectSignal(selectSharedGridVatRateBySelectedStore);
  private readonly uaStockRules: Signal<IUserAccessFeatureStockRules> = this.formStore.selectSignal(
    selectUserAccessFeatureStockRulesForSelectedStore,
  );
  protected readonly isLoading: Signal<boolean> = this.formStore.selectSignal(selectIsPriceBandsLoading);

  protected readonly draftBands = signal<IPriceBand[]>([]);
  protected readonly draftStoreId = signal<string>('');
  protected readonly expandedRuleIndex = signal<number | null>(0);
  protected readonly previewPrice = signal<string>('19.47');
  private readonly hasHydrated = signal<boolean>(false);
  private readonly lastHydratedSnapshot = signal<string>('[]');
  private readonly pendingSaveSnapshot = signal<string | null>(null);
  protected readonly isSaving: Signal<boolean> = this.formStore.selectSignal(selectIsPriceBandsSaving);

  protected readonly hasDraftEdits = computed<boolean>(() => {
    if (!this.hasHydrated()) {
      return false;
    }
    return JSON.stringify(this.draftBands()) !== this.lastHydratedSnapshot();
  });
  protected readonly validationErrors = computed<{error: string; field: string}[]>(() => validatePriceBands(this.draftBands()));
  protected readonly previewResult = computed<number | null>(() => {
    const parsed: number = Number(this.previewPrice());
    if (Number.isNaN(parsed) || parsed < 0) {
      return null;
    }
    const store: IStore = this.selectedStoreSignal();
    if (!store?.storeId) {
      return null;
    }
    const storeVatRates: VatRates = this.vatRates();
    const vatRates: VatRates = isNonEmptyObject(storeVatRates) ? storeVatRates : PRICE_BANDING_PREVIEW_FALLBACK_VAT_RATES;
    const previewItem: {
      latestCost: number;
      nominalGP: number;
      originalValue: Record<string, {value: number}>;
      sellPriIncl1: number;
      vatR: number;
    } = {
      latestCost: 0,
      nominalGP: 0,
      sellPriIncl1: parsed,
      vatR: Number(Object.keys(vatRates)[0] ?? 1),
      originalValue: {
        sellPriIncl1: {value: parsed === 0 ? 1 : 0},
        nominalGP: {value: 0},
      },
    };
    const banded = applyPriceBanding(
      previewItem,
      this.draftBands(),
      ['sellPriIncl1', 'nominalGP'],
      store,
      vatRates,
    );
    return Number(Number(banded.sellPriIncl1).toFixed(2));
  });
  protected readonly previewIsUnchanged = computed<boolean>(() => {
    const result: number | null = this.previewResult();
    if (result === null) {
      return false;
    }
    return result === Number(this.previewPrice());
  });
  protected readonly previewMatchedRuleIndex = computed<number | null>(() => {
    const parsed: number = Number(this.previewPrice());
    if (Number.isNaN(parsed) || parsed < 0) {
      return null;
    }
    const matchIndex: number = this.draftBands().findIndex(
      (band: IPriceBand): boolean => !!band.range && isValueBetweenRanges(parsed, band.range),
    );
    return matchIndex >= 0 ? matchIndex : null;
  });
  protected readonly overlapWarnings = computed<Record<number, string>>(() => getPriceBandOverlapWarnings(this.draftBands()));

  constructor() {
    effect(() => {
      const view: IPriceBandingStoreSettings = this.priceBandingView();
      const storeId: string = this.selectedStoreSignal()?.storeId ?? '';
      const isSaving: boolean = this.isSaving();

      untracked(() => {
        const incomingSnapshot: string = JSON.stringify(view?.bands ?? []);
        const draftSnapshot: string = JSON.stringify(this.draftBands());
        const pendingSnapshot: string | null = this.pendingSaveSnapshot();
        if (pendingSnapshot !== null && !isSaving) {
          this.pendingSaveSnapshot.set(null);
          if (incomingSnapshot === pendingSnapshot) {
            this.lastHydratedSnapshot.set(draftSnapshot);
            this.hasHydrated.set(true);
            return;
          }
        }
        if (this.draftStoreId() === storeId && this.lastHydratedSnapshot() === incomingSnapshot) {
          return;
        }
        if (this.draftStoreId() === storeId && incomingSnapshot === draftSnapshot) {
          this.lastHydratedSnapshot.set(incomingSnapshot);
          this.hasHydrated.set(true);
          return;
        }
        if (this.draftStoreId() === storeId && this.hasDraftEdits()) {
          return;
        }
        this.hydrateFromView(view, storeId);
      });
    });

    effect(() => {
      const ref: SettingsReferenceEnum = this.settingChanged();
      this.formStore.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });

    this.unsavedChangesService.registerComponent(this);
  }

  get haveAnyEditsBeenMade(): boolean {
    return this.hasDraftEdits();
  }

  get canPriceBandingBeEdited(): boolean {
    return this.priceBandingAccess?.canEdit ?? true;
  }

  get canPriceBandingBeSubmitted(): boolean {
    return this.priceBandingAccess?.canSubmit ?? true;
  }

  get canPriceBandingBeViewed(): boolean {
    return this.priceBandingAccess?.canView ?? true;
  }

  get isSubmitDisabled(): boolean {
    return !this.canPriceBandingBeSubmitted
      || !this.haveAnyEditsBeenMade
      || this.validationErrors().length > 0
      || this.isLoading()
      || this.isSaving();
  }

  get priceBandingAccess(): ICrudOptions {
    return this.uaStockRules()?.priceBanding ?? {
      canEdit: true,
      canSubmit: true,
      canView: true,
      isEdited: false,
    };
  }

  ngOnInit(): void {
    this.formStore.dispatch(settingStorePriceBands.getPriceBands({
      params: {storeAction: 'Selected'},
    }));
    this.hydrateFromView(this.priceBandingView(), this.selectedStoreSignal()?.storeId ?? '');
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.unregisterComponent();
  }

  /**
   * Returns the collapsed accordion range label for a draft rule.
   */
  protected describeRuleRange(band: IPriceBand): string {
    return describePriceBandRange(band);
  }

  /**
   * Returns example from/to prices for a single rule.
   */
  protected previewExamples(band: IPriceBand): {from: number; to: number}[] {
    return buildPriceBandPreviewExamples(band);
  }

  protected formatMoney(value: number | undefined): string {
    return formatPriceBandMoney(value);
  }

  protected onPreviewPriceInput(event: Event): void {
    this.previewPrice.set(this.readInputValue(event));
  }

  protected onToggleRule(index: number): void {
    this.expandedRuleIndex.set(this.expandedRuleIndex() === index ? null : index);
  }

  protected onAddRule(): void {
    if (!this.canPriceBandingBeEdited) {
      return;
    }
    const next: IPriceBand[] = [...this.draftBands(), createEmptyPriceBand()];
    this.draftBands.set(next);
    this.expandedRuleIndex.set(next.length - 1);
  }

  protected async onRemoveRule(index: number): Promise<void> {
    if (!this.canPriceBandingBeEdited) {
      return;
    }
    const confirmed: boolean = await this.confirmAction(
      'Remove this rule?',
      ['This price-banding rule will be removed from the draft. Save to apply the change.'],
    );
    if (!confirmed) {
      return;
    }
    const next: IPriceBand[] = this.draftBands().filter((_: IPriceBand, bandIndex: number) => bandIndex !== index);
    this.draftBands.set(next);
    this.expandedRuleIndex.set(next.length ? Math.min(index, next.length - 1) : null);
  }

  /**
   * Clears every draft rule so the user can build the price banding setup from scratch.
   */
  protected async onClearAllRules(): Promise<void> {
    if (!this.canPriceBandingBeEdited || this.draftBands().length === 0) {
      return;
    }
    const confirmed: boolean = await this.confirmAction(
      'Clear all rules?',
      [
        'All price-banding rules for this store will be removed from the draft.',
        'Save to apply the change, or undo to bring them back.',
      ],
      'Clear All Rules',
    );
    if (!confirmed) {
      return;
    }
    this.draftBands.set([]);
    this.expandedRuleIndex.set(null);
  }

  protected async onRestoreGlobal(): Promise<void> {
    if (!this.canPriceBandingBeEdited) {
      return;
    }
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Use Gallix Defaults',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Use Gallix Defaults',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'The current price banding rules for this store will be lost.',
      contextDescription: [
        'This will replace them with the Gallix default price banding settings.',
      ],
      cssClass: ['shared-basic-modal-css-small'],
    };
    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    if (result.role !== 'modal-accept') {
      return;
    }
    this.draftBands.set(clonePriceBands(this.globalBands()));
    this.expandedRuleIndex.set(this.draftBands().length ? 0 : null);
  }

  protected onRevertForm(): void {
    this.hydrateFromView(this.priceBandingView(), this.selectedStoreSignal()?.storeId ?? '');
  }

  protected onSubmitForm(): void {
    if (this.isSubmitDisabled) {
      return;
    }
    const bands: IPriceBand[] = clonePriceBands(this.draftBands());
    this.pendingSaveSnapshot.set(JSON.stringify(bands));
    this.formStore.dispatch(settingStorePriceBands.setPriceBands({
      bands,
    }));
  }

  protected onRangeValueChange(index: number, edge: TRangeEdge, event: Event): void {
    const rawValue: string = this.readInputValue(event);
    const value: number = Number(rawValue);
    if (Number.isNaN(value)) {
      return;
    }
    this.patchRange(index, edge, {v: value});
  }

  protected onRangeInclusiveChange(index: number, edge: TRangeEdge, event: Event): void {
    this.patchRange(index, edge, {inc: this.readCheckboxValue(event)});
  }

  protected onToggleRandSnaps(index: number, enabled: boolean): void {
    const band: IPriceBand = this.draftBands()[index];
    if (enabled) {
      this.patchBand(index, {
        randSnap: band.randSnap ?? {
          digits: 1,
          snaps: [createEmptyPriceBandSnap(9)],
        },
      });
      return;
    }
    const copy: IPriceBand = getDeepCopyOfObject(band);
    delete copy.randSnap;
    this.replaceBand(index, copy);
  }

  protected onRandDigitsChange(index: number, delta: number): void {
    const band: IPriceBand = this.draftBands()[index];
    const current: number = band.randSnap?.digits ?? 1;
    const nextDigits: number = Math.min(4, Math.max(1, current + delta));
    this.patchBand(index, {
      randSnap: {
        digits: nextDigits,
        snaps: band.randSnap?.snaps ?? [createEmptyPriceBandSnap(Math.pow(10, nextDigits) - 1)],
      },
    });
  }

  protected onAddSnap(index: number, kind: TSnapKind): void {
    const band: IPriceBand = this.draftBands()[index];
    if (kind === 'cents') {
      this.patchBand(index, {
        centSnaps: [...(band.centSnaps ?? []), createEmptyPriceBandSnap(99)],
      });
      return;
    }
    const digits: number = band.randSnap?.digits ?? 1;
    this.patchBand(index, {
      randSnap: {
        digits,
        snaps: [...(band.randSnap?.snaps ?? []), createEmptyPriceBandSnap(Math.pow(10, digits) - 1)],
      },
    });
  }

  protected onRemoveSnap(index: number, snapIndex: number, kind: TSnapKind): void {
    const band: IPriceBand = this.draftBands()[index];
    if (kind === 'cents') {
      this.patchBand(index, {
        centSnaps: (band.centSnaps ?? []).filter((_: IPriceBandSnap, current: number) => current !== snapIndex),
      });
      return;
    }
    this.patchBand(index, {
      randSnap: {
        digits: band.randSnap?.digits ?? 1,
        snaps: (band.randSnap?.snaps ?? []).filter((_: IPriceBandSnap, current: number) => current !== snapIndex),
      },
    });
  }

  protected onSnapRangeChange(
    index: number,
    snapIndex: number,
    kind: TSnapKind,
    edge: TRangeEdge,
    event: Event,
  ): void {
    const value: number = Number(this.readInputValue(event));
    if (Number.isNaN(value)) {
      return;
    }
    this.patchSnap(index, snapIndex, kind, (snap: IPriceBandSnap): IPriceBandSnap => ({
      ...snap,
      range: {
        ...snap.range,
        [edge]: {
          ...(snap.range?.[edge] ?? {v: 0, inc: true}),
          v: value,
        },
      },
    }));
  }

  protected onSnapValueChange(index: number, snapIndex: number, kind: TSnapKind, event: Event): void {
    const value: number = Number(this.readInputValue(event));
    if (Number.isNaN(value)) {
      return;
    }
    this.patchSnap(index, snapIndex, kind, (snap: IPriceBandSnap): IPriceBandSnap => ({
      ...snap,
      v: value,
    }));
  }

  async onShowModalUnsavedChanges(): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };
    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }

  private hydrateFromView(view: IPriceBandingStoreSettings, storeId: string): void {
    const bands: IPriceBand[] = clonePriceBands(view?.bands);
    this.draftStoreId.set(storeId);
    this.draftBands.set(bands);
    this.lastHydratedSnapshot.set(JSON.stringify(bands));
    this.expandedRuleIndex.set(bands.length > 0 ? 0 : null);
    this.hasHydrated.set(true);
  }

  private patchBand(index: number, patch: Partial<IPriceBand>): void {
    const bands: IPriceBand[] = clonePriceBands(this.draftBands());
    bands[index] = {
      ...bands[index],
      ...patch,
    };
    this.draftBands.set(bands);
  }

  private replaceBand(index: number, band: IPriceBand): void {
    const bands: IPriceBand[] = clonePriceBands(this.draftBands());
    bands[index] = band;
    this.draftBands.set(bands);
  }

  private patchRange(index: number, edge: TRangeEdge, patch: Partial<IRange['start']>): void {
    const band: IPriceBand = this.draftBands()[index];
    const current: IRange = band.range ?? {};
    this.patchBand(index, {
      range: {
        ...current,
        [edge]: {
          ...(current[edge] ?? {v: 0, inc: true}),
          ...patch,
        },
      },
    });
  }

  private patchSnap(
    index: number,
    snapIndex: number,
    kind: TSnapKind,
    updater: (snap: IPriceBandSnap) => IPriceBandSnap,
  ): void {
    const band: IPriceBand = this.draftBands()[index];
    if (kind === 'cents') {
      const snaps: IPriceBandSnap[] = getDeepCopyOfObject(band.centSnaps ?? []) ?? [];
      snaps[snapIndex] = updater(snaps[snapIndex]);
      this.patchBand(index, {centSnaps: snaps});
      return;
    }
    const snaps: IPriceBandSnap[] = getDeepCopyOfObject(band.randSnap?.snaps ?? []) ?? [];
    snaps[snapIndex] = updater(snaps[snapIndex]);
    this.patchBand(index, {
      randSnap: {
        digits: band.randSnap?.digits ?? 1,
        snaps,
      },
    });
  }

  private readInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  private readCheckboxValue(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  private async confirmAction(title: string, description: string[], acceptText: string = 'Continue'): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: acceptText,
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: title,
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextDescription: description,
      cssClass: ['shared-basic-modal-css-small'],
    };
    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }
}
